import {
  SectionHeader,
  ListItem,
  AHText,
  TouchableBox,
  AHIcon,
  AHSeparator,
} from "arkh";
import { FormattedMessage } from "react-intl";
import { MoreButton } from "../../../../common/MoreButton";
import { OrgUserEditAccessSelect } from "./OrgUserEditAccessSelect";
import { OrgUserEditRoleSelect } from "./OrgUserEditRoleSelect";
import { OrgUserRolesRow } from "./OrgUserRolesRow";
import { WorkspaceRoleType } from "./__generated__/OrgUserEditScreenQuery.graphql";
import React, { useMemo } from "react";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { graphql, useFragment } from "react-relay";
import { WorkspaceRolesSelector_workspaces$key } from "./__generated__/WorkspaceRolesSelector_workspaces.graphql";

export type WorkspaceRole = {
  roleType: WorkspaceRoleType;
  workspace:
    | {
        id: string;
        name: string;
        subWorkspaceCount: number;
      }
    | undefined;
};
export function WorkspaceRolesSelector(props: {
  roles: WorkspaceRole[];
  setRoles: React.Dispatch<React.SetStateAction<WorkspaceRole[]>>;
  workspaces: WorkspaceRolesSelector_workspaces$key;
}) {
  const { roles, setRoles } = props;
  const workspaceIdsFromRoles = useMemo(
    () =>
      roles.flatMap((r) =>
        r.workspace?.id !== undefined ? [r.workspace.id] : []
      ),
    [roles]
  );

  const intl_tokens = useIntlTokens();

  const workspaces = useFragment(
    graphql`
      fragment WorkspaceRolesSelector_workspaces on Workspace
      @relay(plural: true) {
        ...OrgUserEditAccessSelect_workspaces
      }
    `,
    props.workspaces
  );

  return (
    <>
      <SectionHeader>
        <SectionHeader.Title>
          <FormattedMessage
            defaultMessage="Workspace and Roles"
            description="OrgUserEdit Workspace and Roles section header"
          />
        </SectionHeader.Title>
        <SectionHeader.Description>
          <FormattedMessage
            defaultMessage="Workspace access & role is inherited by all workspaces below in the hierachy"
            description="OrgUserEdit Workspace and Roles description"
          />
        </SectionHeader.Description>
      </SectionHeader>
      <ListItem
        isFirst
        isLast={roles.length === 0}
        disabled
        border="border-primary"
      >
        <OrgUserRolesRow
          access={
            <AHText type="subtitle">
              <FormattedMessage defaultMessage="Access" description="Access" />
            </AHText>
          }
          role={
            <AHText type="subtitle" color="text-secondary">
              <FormattedMessage defaultMessage="Role" description="Role" />
            </AHText>
          }
          action={
            <TouchableBox
              onPress={() => {
                setRoles((r) => [
                  ...r,
                  {
                    workspace: undefined,
                    roleType: "ADMIN",
                  },
                ]);
              }}
            >
              <AHIcon type="add-circle" color="text-secondary" />
            </TouchableBox>
          }
        />
      </ListItem>
      {roles.map((role, index) => (
        <>
          <AHSeparator />
          <ListItem
            disabled
            isLast={roles.length - 1 === index}
            border="border-primary"
          >
            <OrgUserRolesRow
              access={
                <OrgUserEditAccessSelect
                  workspaces={workspaces}
                  selectedWorkspace={role.workspace}
                  disabledIds={workspaceIdsFromRoles}
                  onSelectWorkspace={(workspace) => {
                    setRoles((r) =>
                      r.map((wr, i) =>
                        i === index
                          ? {
                              workspace,
                              roleType: wr.roleType,
                            }
                          : wr
                      )
                    );
                  }}
                />
              }
              role={
                <OrgUserEditRoleSelect
                  onSelectRole={(role) => {
                    setRoles((r) =>
                      r.map((wr, i) =>
                        i === index
                          ? {
                              ...wr,
                              roleType: role,
                            }
                          : wr
                      )
                    );
                  }}
                  selectedRole={role.roleType}
                />
              }
              action={
                <MoreButton
                  items={[
                    {
                      icon: "trash",
                      label: intl_tokens.Delete,
                      color: "negative",
                      type: "button",
                      onPress: () => {
                        setRoles((r) => r.filter((_, i) => i !== index));
                      },
                    },
                  ]}
                >
                  {(onOpen) => (
                    <TouchableBox onPress={onOpen}>
                      <AHIcon type="more-vertical" color="text-secondary" />
                    </TouchableBox>
                  )}
                </MoreButton>
              }
            />
            {(role.workspace?.subWorkspaceCount ?? 0) > 0 ? (
              <OrgUserRolesRow
                access={
                  <AHText color="text-washed" type="subtitle" spaceTop="small">
                    <FormattedMessage
                      defaultMessage="{count, plural, one {Includes 1 workspace below} other {Includes # workspaces}}"
                      description="OrgUserEdit has workspaces below"
                      values={{ count: role.workspace?.subWorkspaceCount ?? 0 }}
                    />
                  </AHText>
                }
                role={null}
                action={null}
              />
            ) : null}
          </ListItem>
        </>
      ))}
    </>
  );
}
